import { useEffect, useState } from "react";
import { X, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import AgentActionCards from "@/components/AgentActionCards";

/**
 * ProactiveWhisper — chuchotement discret de Noam sur une page module.
 *
 * V23.15.28 — Touche 3 du dispositif pro-actif :
 *  - Se monte sur une page module (ex: Risques, Annuaire, Origination…)
 *  - Fetch /workspace/intelligence/v2/module-whisper/{module}
 *  - Si le brief pro-actif contient au moins 1 action ciblant ce module,
 *    affiche une bannière compacte au-dessus du contenu de la page.
 *  - Sinon : renvoie null (silence total, aucune régression visuelle).
 *  - Bouton de fermeture par-session (sessionStorage) pour ne pas insister.
 *
 * Coût cognitif visé : ZÉRO. L'utilisateur voit soit rien, soit exactement
 * l'action qu'il peut faire immédiatement (Pré-remplir · Créer · Activer).
 *
 * Usage :
 *   <ProactiveWhisper module="risques" />
 */
export default function ProactiveWhisper({ module }) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Persistance de la fermeture pour la session courante
    try {
      const key = `noam_whisper_dismissed_${module}`;
      if (sessionStorage.getItem(key) === "1") {
        setDismissed(true);
        return;
      }
    } catch { /* ignore */ }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/workspace/intelligence/v2/module-whisper/${module}`);
        if (cancelled) return;
        const actions = Array.isArray(r.data?.actions) ? r.data.actions : [];
        const hint = r.data?.text_hint || "";
        if (actions.length > 0) {
          setData({ actions, hint });
        }
      } catch {
        /* silencieux */
      }
    })();
    return () => { cancelled = true; };
  }, [module]);

  if (dismissed || !data || !data.actions?.length) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(`noam_whisper_dismissed_${module}`, "1");
    } catch { /* ignore */ }
    setDismissed(true);
  };

  const handleAction = () => {
    // Une fois qu'une action a été activée, on ferme le whisper (pas de bruit résiduel)
    handleDismiss();
  };

  return (
    <div
      className="mb-4 rounded-xl border border-[#EF9F27]/25 bg-gradient-to-br from-[#EF9F27]/[0.06] to-transparent p-3 backdrop-blur-sm relative"
      data-testid={`proactive-whisper-${module}`}
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 w-6 h-6 rounded-md hover:bg-white/5 flex items-center justify-center text-zinc-500 hover:text-zinc-300 transition-colors"
        title="Fermer"
        data-testid={`proactive-whisper-dismiss-${module}`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-start gap-2 pr-6">
        <div className="w-6 h-6 rounded-md bg-[#EF9F27]/15 border border-[#EF9F27]/30 flex items-center justify-center shrink-0 mt-0.5">
          <Sparkles className="w-3.5 h-3.5 text-[#EF9F27]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-[#EF9F27] uppercase tracking-wider font-medium">
            Noam · chuchotement
          </div>
          {data.hint && (
            <div className="text-xs text-zinc-300 mt-0.5">{data.hint}</div>
          )}
          <div className="mt-2">
            <AgentActionCards actions={data.actions} onAction={handleAction} />
          </div>
        </div>
      </div>
    </div>
  );
}
